import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { useEffect, useRef, useState, useCallback, memo } from "react";
import { useLocationSearch } from "@/hooks/use-location-search";
import { IMAGES } from "../../svgImagesByType";
import type { ImageTypesEnum } from "@/pages/home/types/directionsTypes";

type SelectRouteSearchProps = {
  setValue: (value: string) => void;
  value: string;
  clearValue: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  dotColor: string;
};

const SelectRouteSearch = memo(function SelectRouteSearch({
  setValue,
  value,
  clearValue,
  containerRef,
  dotColor,
}: SelectRouteSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [containerRef]);

  const { results } = useLocationSearch(search);

  // Sync search with external value
  useEffect(() => {
    setSearch(value);
  }, [value]);

  // Reset highlight when results change
  useEffect(() => {
    if (results.length > 0 && isOpen) {
      setHighlightedIndex(0);
    }
  }, [results, isOpen]);

  // Click outside to close
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (place: { name: string; imageType: string }) => {
      setIsOpen(false);
      setSearch(place.name);
      setValue(place.name);
    },
    [setValue],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < results.length - 1 ? prev + 1 : 0,
        );
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : results.length - 1,
        );
      }

      if (e.key === "Enter") {
        e.preventDefault();
        if (results[highlightedIndex]) {
          e.currentTarget.blur();
          handleSelect(results[highlightedIndex]);
        }
      }

      if (e.key === "Escape") {
        e.currentTarget.blur();
        setIsOpen(false);
      }
    },
    [isOpen, results, highlightedIndex, handleSelect],
  );

  const handleClear = useCallback(() => {
    setSearch("");
    clearValue();
    setIsOpen(false);
  }, [clearValue]);

  const handleFocus = useCallback(() => {
    updateDropdownPosition();
    setIsOpen(true);
    if (results.length > 0) {
      setHighlightedIndex(0);
    }
  }, [updateDropdownPosition, results.length]);

  return (
    <div ref={wrapperRef} className="relative flex-1 flex items-center gap-3">
      <div
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: dotColor }}
      />
      <Input
        className="rounded-none flex-1 bg-transparent border-none p-0 h-auto text-sm font-medium text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0"
        value={search}
        onChange={(e) => {
          setIsOpen(true);
          setSearch(e.target.value);
        }}
        onFocus={handleFocus}
        onClick={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder="Выберите точку..."
      />

      <button
        onClick={handleClear}
        className={`w-[22px] h-[22px] flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition cursor-pointer ${
          search ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <X className="h-[14px] w-[14px]" />
      </button>

      {isOpen && results.length > 0 && (
        <ul
          className="py-1 shadow-lg bg-white rounded-lg z-[200] border border-gray-100"
          style={dropdownStyle}
        >
          {results.map((place, index) => (
            <li
              key={`${place.name}-${place.imageType}-${index}`}
              className={`flex items-center py-2.5 px-4 cursor-pointer transition ${
                index === highlightedIndex
                  ? "bg-gray-100 text-gray-900"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => handleSelect(place)}
            >
              <div className="flex items-center gap-3 w-full">
                {place.imageType && IMAGES[place.imageType as ImageTypesEnum]}
                <span className="text-sm font-medium">{place.name}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default SelectRouteSearch;
